// utils/currency.ts
//
// Two distinct notions of "which currencies may I pick", and they must not be
// conflated:
//
//   Inherited — a customer takes its owning entity's currency. The list comes
//   from `getAvailableCurrencies()` (`GET /currencies/available`), which returns
//   the viewer entity's own currency first, then the default currency of each
//   operational country. Deliberately narrow: TVNZ gets [NZD] and the picker is
//   hidden entirely, because there is no decision to make. See ClientDrawer.
//
//   Declared — a superuser sets an entity's own settlement currency. That cannot
//   be derived territorially: Teads is an HK/SG entity that settles in USD, and
//   no rule keyed off HK or SG will ever offer USD. At create time there is no
//   entity to derive from at all, so no server-scoped list can help either. The
//   declaring forms therefore show the full ISO 4217 list, which is what this
//   module provides. Superuser-only screens, so a long list is no hardship.
//
// The list is sourced from the browser rather than a hand-maintained table or an
// extra endpoint. `Intl.supportedValuesOf` is ES2022 and ships everywhere we
// support; symbols and display names come from Intl too, so nothing here needs
// touching when a currency is added, renamed or redenominated.

import { FALLBACK_CURRENCY } from './formatters';

export interface CurrencyOption {
    code: string;
    label: string;
    symbol: string;
}

/**
 * The currency a monetary form or table renders in, as delivered by the backend's
 * territorial context (`CurrencyContextResponse`). Distinct from `CurrencyOption`
 * above, which is one entry in a list still to be chosen from — this is the answer,
 * already decided for a given customer.
 */
export interface Currency {
    code: string;
    symbol: string;
    abbrev: string;
    fraction_digits: number;
}

/**
 * Placeholder for the window before a customer's territorial context arrives.
 * `TerritorialContextResponse.currency` is required on the backend, so once the
 * fetch lands there is always a real answer — this is a loading state, never a
 * result.
 *
 * It carries `FALLBACK_CURRENCY` so figures format consistently with the rest of
 * the app, but deliberately no `abbrev`/`symbol`: a hardcoded "NZ$" beside a CPM
 * field is a label that lies to an Australian or Indian customer, and it was
 * hand-written in three separate places before this constant existed. Consumers
 * must render the tag conditionally rather than interpolating it blind.
 */
export const PENDING_CURRENCY: Currency = {
    code: FALLBACK_CURRENCY,
    symbol: '',
    abbrev: '',
    fraction_digits: 2,
};

/** Markets we care about, floated to the top so they are one keystroke away. */
const PRIORITY_CODES = ['NZD', 'AUD', 'USD', 'SGD', 'HKD', 'MYR', 'INR', 'JPY'];

/** Used only if `Intl.supportedValuesOf` is unavailable — enough to keep the forms usable. */
const FALLBACK_CODES = [...PRIORITY_CODES, 'GBP', 'EUR', 'CAD', 'CNY', 'IDR', 'PHP', 'THB', 'ZAR'];

/**
 * The disambiguated symbol ("US$", "A$", "HK$") rather than the narrow one,
 * which collapses every dollar currency to a bare "$" and is useless in a picker.
 */
const symbolFor = (code: string): string => {
    try {
        const parts = new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).formatToParts(0);
        return parts.find((p) => p.type === 'currency')?.value ?? code;
    } catch {
        return code;
    }
};

const nameFor = (code: string, display: Intl.DisplayNames | null): string => {
    try {
        return display?.of(code) ?? code;
    } catch {
        return code;
    }
};

let cached: CurrencyOption[] | null = null;

/**
 * Every ISO 4217 currency, priority markets first, then alphabetical by code.
 * Computed once per session — the underlying Intl data never changes at runtime.
 */
export const getAllCurrencyOptions = (): CurrencyOption[] => {
    if (cached) return cached;

    let codes: string[];
    try {
        codes = Intl.supportedValuesOf('currency');
    } catch {
        codes = FALLBACK_CODES;
    }
    if (!codes.length) codes = FALLBACK_CODES;

    let display: Intl.DisplayNames | null = null;
    try {
        display = new Intl.DisplayNames(undefined, { type: 'currency' });
    } catch {
        display = null;
    }

    const rank = (code: string) => {
        const i = PRIORITY_CODES.indexOf(code);
        return i === -1 ? PRIORITY_CODES.length : i;
    };

    cached = codes
        .map((code) => ({ code, label: `${code} — ${nameFor(code, display)}`, symbol: symbolFor(code) }))
        .sort((a, b) => rank(a.code) - rank(b.code) || a.code.localeCompare(b.code));

    return cached;
};

/** Look up a single option, synthesising one for codes Intl does not recognise. */
export const currencyOptionFor = (code: string | null | undefined): CurrencyOption | null => {
    if (!code) return null;
    return (
        getAllCurrencyOptions().find((c) => c.code === code) ?? {
            code,
            label: code,
            symbol: code,
        }
    );
};
